import { FC, useEffect, useState } from 'react';
import { Loader } from 'lucide-react';

import { BreadcrumbItemType, ExplorerItem } from '@/types/file-explorer-types';

import { cn } from '@/lib/utils';

import { useCalculateRowsPerPage } from '@/hooks/use-calculate-rows-per-page';
import { useIsMobile } from '@/hooks/use-mobile';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';

import { ROW_HEIGHT_SMALL } from '@/constants/global-constants';
import { ExplorerItemType } from '@/constants/table-columns-config';

import { FileExplorer } from './FileExplorer';
import FileSelectionHeaderMobile from './FileSelectionHeaderMobile';
import { ImageState } from './ImageCompareDialog';
import ImageDisplay from './ImageDisplay';

interface ExplorerViewProps {
  leftExplorer: {
    isEnabled: boolean;
    currentUUID: string;
    entityType: ExplorerItemType;
  };
  rightExplorer: {
    isEnabled: boolean;
    currentUUID: string;
    entityType: ExplorerItemType;
  };
  onLeftUpdate: React.Dispatch<
    React.SetStateAction<{
      isEnabled: boolean;
      currentUUID: string;
      entityType: ExplorerItemType;
    }>
  >;
  onRightUpdate: React.Dispatch<
    React.SetStateAction<{
      isEnabled: boolean;
      currentUUID: string;
      entityType: ExplorerItemType;
    }>
  >;
  onImageSelect: (side: 'left' | 'right') => (uuid: string) => void;
  leftImage: ImageState | null;
  rightImage: ImageState | null;
  handleFileSelect: (side: 'left' | 'right') => () => void;
}

const FILES_BATCH_SIZE = 20;

const ExplorerView: FC<ExplorerViewProps> = ({
  leftExplorer,
  rightExplorer,
  onLeftUpdate,
  onRightUpdate,
  onImageSelect,
  handleFileSelect,
  leftImage,
  rightImage,
}) => {
  const { getStateByType, getBreadcrumbItems } = useFileExplorerStore();
  const isMobile = useIsMobile();

  const { getAgreement, getAutoFetchFiles } = useApiManagerStore();

  const [currentItemsLeft, setCurrentItemsLeft] = useState<ExplorerItem[]>([]);
  const [breadcrumbsLeft, setBreadcrumbsLeft] = useState<BreadcrumbItemType[]>(
    [],
  );
  const [currentItemsRight, setCurrentItemsRight] = useState<ExplorerItem[]>(
    [],
  );
  const [breadcrumbsRight, setBreadcrumbsRight] = useState<
    BreadcrumbItemType[]
  >([]);

  const rowsPerPage = useCalculateRowsPerPage(ROW_HEIGHT_SMALL);

  // Loading and pagination states
  const [isLoadingLeft, setIsLoadingLeft] = useState(false);
  const [isLoadingRight, setIsLoadingRight] = useState(false);
  const [isLoadingMoreLeft, setIsLoadingMoreLeft] = useState(false);
  const [isLoadingMoreRight, setIsLoadingMoreRight] = useState(false);
  const [hasMoreFilesLeft, setHasMoreFilesLeft] = useState(false);
  const [hasMoreFilesRight, setHasMoreFilesRight] = useState(false);
  const [leftLoadedCount, setLeftLoadedCount] = useState(0);
  const [rightLoadedCount, setRightLoadedCount] = useState(0);
  const [allFileUuidsLeft, setAllFileUuidsLeft] = useState<string[]>([]);
  const [allFileUuidsRight, setAllFileUuidsRight] = useState<string[]>([]);

  // New pagination states for non-file entity types
  const [leftPageIndex, setLeftPageIndex] = useState(0);
  const [rightPageIndex, setRightPageIndex] = useState(0);
  const [leftTotalCount, setLeftTotalCount] = useState(0);
  const [rightTotalCount, setRightTotalCount] = useState(0);

  useEffect(() => {
    const fetchLeft = async () => {
      setIsLoadingLeft(true);
      try {
        let items: ExplorerItem[] = [];
        if (leftExplorer.entityType === ExplorerItemType.FILE) {
          const fileUuids =
            (await getAgreement(leftExplorer.currentUUID)).files || [];

          setAllFileUuidsLeft(fileUuids);

          // Take only the first batch
          const fileUuidsBatch = fileUuids.slice(0, FILES_BATCH_SIZE);
          setLeftLoadedCount(fileUuidsBatch.length);
          setHasMoreFilesLeft(fileUuids.length > fileUuidsBatch.length);

          if (fileUuidsBatch.length > 0) {
            const files = await getAutoFetchFiles(fileUuidsBatch);
            items = files.map((file) => ({
              ...file,
              breadcrumb_name: file.name,
              parentId: leftExplorer.currentUUID,
              entityType: ExplorerItemType.FILE,
            }));
          }
        } else {
          // Reset pagination state for new entity/UUID
          setLeftPageIndex(0);

          const state = await getStateByType(
            leftExplorer.entityType,
            leftExplorer.currentUUID,
            {
              pageIndex: 0 / rowsPerPage,
              pageSize: rowsPerPage,
            },
          );
          items = state.rootIds.map((id) => state.entities[id]);

          setLeftTotalCount(state.totalCount);
          setHasMoreFilesLeft(items.length === rowsPerPage);
        }

        const breadcrumbs = await getBreadcrumbItems(
          leftExplorer.entityType,
          leftExplorer.currentUUID,
        );
        setCurrentItemsLeft(items);
        setBreadcrumbsLeft(breadcrumbs);
      } catch (error) {
        console.error('Error loading left explorer items:', error);
      } finally {
        setIsLoadingLeft(false);
      }
    };
    fetchLeft();
  }, [leftExplorer.entityType, leftExplorer.currentUUID, rowsPerPage]);

  useEffect(() => {
    const fetchRight = async () => {
      setIsLoadingRight(true);
      try {
        let items: ExplorerItem[] = [];
        if (rightExplorer.entityType === ExplorerItemType.FILE) {
          const fileUuids =
            (await getAgreement(rightExplorer.currentUUID)).files || [];

          setAllFileUuidsRight(fileUuids);

          // Take only the first batch
          const fileUuidsBatch = fileUuids.slice(0, FILES_BATCH_SIZE);
          setRightLoadedCount(fileUuidsBatch.length);
          setHasMoreFilesRight(fileUuids.length > fileUuidsBatch.length);

          if (fileUuidsBatch.length > 0) {
            const files = await getAutoFetchFiles(fileUuidsBatch);
            items = files.map((file) => ({
              ...file,
              breadcrumb_name: file.name,
              parentId: rightExplorer.currentUUID,
              entityType: ExplorerItemType.FILE,
            }));
          }
        } else {
          // Reset pagination state for new entity/UUID
          setRightPageIndex(0);
          const state = await getStateByType(
            rightExplorer.entityType,
            rightExplorer.currentUUID,
            {
              pageIndex: 0 / rowsPerPage,
              pageSize: rowsPerPage,
            },
          );
          items = state.rootIds.map((id) => state.entities[id]);

          setRightTotalCount(state.totalCount);
          setHasMoreFilesRight(items.length === rowsPerPage);
        }

        const breadcrumbs = await getBreadcrumbItems(
          rightExplorer.entityType,
          rightExplorer.currentUUID,
        );
        setCurrentItemsRight(items);
        setBreadcrumbsRight(breadcrumbs);
      } catch (error) {
        console.error('Error loading right explorer items:', error);
      } finally {
        setIsLoadingRight(false);
      }
    };
    fetchRight();
  }, [rightExplorer.entityType, rightExplorer.currentUUID, rowsPerPage]);

  const handleLoadMoreLeft = async () => {
    if (isLoadingMoreLeft || !hasMoreFilesLeft) return;

    setIsLoadingMoreLeft(true);
    try {
      if (leftExplorer.entityType === ExplorerItemType.FILE) {
        const nextBatch = allFileUuidsLeft.slice(
          leftLoadedCount,
          leftLoadedCount + FILES_BATCH_SIZE,
        );

        if (nextBatch.length > 0) {
          const moreFiles = await getAutoFetchFiles(nextBatch);
          const moreItems = moreFiles.map((file) => ({
            ...file,
            breadcrumb_name: file.name,
            parentId: leftExplorer.currentUUID,
            entityType: ExplorerItemType.FILE,
          }));

          setCurrentItemsLeft((prev) => [...prev, ...moreItems]);
          const newLoadedCount = leftLoadedCount + nextBatch.length;
          setLeftLoadedCount(newLoadedCount);
          setHasMoreFilesLeft(allFileUuidsLeft.length > newLoadedCount);
        } else {
          setHasMoreFilesLeft(false);
        }
      } else {
        // Handle pagination for non-file entity types
        const nextPageIndex = leftPageIndex + 1;
        const state = await getStateByType(
          leftExplorer.entityType,
          leftExplorer.currentUUID,
          { pageIndex: nextPageIndex, pageSize: rowsPerPage },
        );
        const moreItems = state.rootIds.map((id) => state.entities[id]);

        if (moreItems.length > 0) {
          setCurrentItemsLeft((prev) => [...prev, ...moreItems]);
          setLeftPageIndex(nextPageIndex);

          const totalLoadedItems = (nextPageIndex + 1) * rowsPerPage;
          setHasMoreFilesLeft(
            totalLoadedItems < leftTotalCount &&
              moreItems.length >= rowsPerPage,
          );
        } else {
          setHasMoreFilesLeft(false);
        }
      }
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setIsLoadingMoreLeft(false);
    }
  };

  const handleLoadMoreRight = async () => {
    if (isLoadingMoreRight || !hasMoreFilesRight) return;

    setIsLoadingMoreRight(true);
    try {
      if (rightExplorer.entityType === ExplorerItemType.FILE) {
        const nextBatch = allFileUuidsRight.slice(
          rightLoadedCount,
          rightLoadedCount + FILES_BATCH_SIZE,
        );

        if (nextBatch.length > 0) {
          const moreFiles = await getAutoFetchFiles(nextBatch);
          const moreItems = moreFiles.map((file) => ({
            ...file,
            breadcrumb_name: file.name,
            parentId: rightExplorer.currentUUID,
            entityType: ExplorerItemType.FILE,
          }));

          setCurrentItemsRight((prev) => [...prev, ...moreItems]);
          const newLoadedCount = rightLoadedCount + nextBatch.length;
          setRightLoadedCount(newLoadedCount);
          setHasMoreFilesRight(allFileUuidsRight.length > newLoadedCount);
        } else {
          setHasMoreFilesRight(false);
        }
      } else {
        // Handle pagination for non-file entity types
        const nextPageIndex = rightPageIndex + 1;
        const state = await getStateByType(
          rightExplorer.entityType,
          rightExplorer.currentUUID,
          { pageIndex: nextPageIndex, pageSize: rowsPerPage },
        );
        const moreItems = state.rootIds.map((id) => state.entities[id]);

        if (moreItems.length > 0) {
          setCurrentItemsRight((prev) => [...prev, ...moreItems]);
          setRightPageIndex(nextPageIndex);

          const totalLoadedItems = (nextPageIndex + 1) * rowsPerPage;
          setHasMoreFilesRight(
            totalLoadedItems < rightTotalCount &&
              moreItems.length >= rowsPerPage,
          );
        } else {
          setHasMoreFilesRight(false);
        }
      }
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setIsLoadingMoreRight(false);
    }
  };

  // Loading indicator component
  const LoadingIndicator = () => (
    <div className="flex h-full flex-1 items-center justify-center p-4">
      <div className="flex flex-col items-center gap-2">
        <Loader className="h-6 w-6 animate-spin-slow"></Loader>
        <span className="text-sm text-gray-400">Завантаження файлів...</span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-1 flex-col gap-4 sm:flex-none sm:flex-row">
      {isMobile && (
        <FileSelectionHeaderMobile
          onLeftSelect={handleFileSelect('left')}
          onRightSelect={handleFileSelect('right')}
          leftFilename={leftImage?.name}
          rightFilename={rightImage?.name}
        />
      )}
      <div
        className={cn(
          'max-h-[80vh] min-h-[50vw] flex-1 overflow-hidden rounded-md border border-dashed border-separator sm:min-h-[600px]',
          isMobile && 'order-1',
        )}
      >
        {leftExplorer.isEnabled && leftImage === null && (
          <div
            className={cn(
              'max-h-[40vh] overflow-y-auto sm:max-h-[600px]',
              isLoadingLeft && 'h-full',
            )}
          >
            {isLoadingLeft ? (
              <LoadingIndicator />
            ) : (
              <>
                <FileExplorer
                  items={currentItemsLeft}
                  breadcrumbs={breadcrumbsLeft}
                  entityType={leftExplorer.entityType}
                  setCurrentUUID={(uuid) =>
                    onLeftUpdate((prev) => ({ ...prev, currentUUID: uuid }))
                  }
                  setCurrentEntityType={(type) =>
                    onLeftUpdate((prev) => ({ ...prev, entityType: type }))
                  }
                  isSmall
                  rowsPerPage={rowsPerPage}
                  onImageClick={onImageSelect('left')}
                  totalCount={leftTotalCount}
                />
                {hasMoreFilesLeft && (
                  <div className="flex justify-center p-2.5">
                    <button
                      onClick={handleLoadMoreLeft}
                      disabled={isLoadingMoreLeft}
                      className="hover:text-primary/80 rounded-md px-4 py-2 text-sm font-medium text-primary hover:bg-gray-100 focus:outline-none"
                    >
                      {isLoadingMoreLeft ? (
                        <div className="flex items-center gap-2">
                          <Loader className="h-4 w-4 animate-spin-slow"></Loader>
                          <span>Завантаження...</span>
                        </div>
                      ) : (
                        'Завантажити ще'
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {leftImage !== null && (
          <ImageDisplay image={leftImage} onTransform={() => {}} />
        )}
      </div>
      <div
        className={cn(
          'max-h-[80vh] min-h-[50vw] flex-1 overflow-hidden rounded-md border border-gray-200 sm:min-h-[600px]',
          isMobile && 'order-3',
        )}
      >
        {rightExplorer.isEnabled && rightImage === null && (
          <div
            className={cn(
              'max-h-[40vh] overflow-y-auto sm:max-h-[600px]',
              isLoadingRight && 'h-full',
            )}
          >
            {isLoadingRight ? (
              <LoadingIndicator />
            ) : (
              <>
                <FileExplorer
                  items={currentItemsRight}
                  breadcrumbs={breadcrumbsRight}
                  entityType={rightExplorer.entityType}
                  setCurrentUUID={(uuid) => {
                    onRightUpdate((prev) => ({ ...prev, currentUUID: uuid }));
                  }}
                  setCurrentEntityType={(type) =>
                    onRightUpdate((prev) => ({ ...prev, entityType: type }))
                  }
                  isSmall
                  rowsPerPage={rowsPerPage}
                  onImageClick={onImageSelect('right')}
                  totalCount={rightTotalCount}
                />
                {hasMoreFilesRight && (
                  <div className="flex justify-center p-2.5">
                    <button
                      onClick={handleLoadMoreRight}
                      disabled={isLoadingMoreRight}
                      className="hover:text-primary/80 rounded-md px-4 py-2 text-sm font-medium text-primary hover:bg-gray-100 focus:outline-none"
                    >
                      {isLoadingMoreRight ? (
                        <div className="flex items-center gap-2">
                          <Loader className="h-4 w-4 animate-spin-slow"></Loader>
                          <span>Завантаження...</span>
                        </div>
                      ) : (
                        'Завантажити ще'
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {rightImage !== null && (
          <ImageDisplay image={rightImage} onTransform={() => {}} />
        )}
        {!rightExplorer.isEnabled && (
          <div
            className="flex h-full items-center justify-center text-gray-400 hover:cursor-pointer"
            onClick={() => {
              onRightUpdate((prev) => ({ ...prev, isEnabled: true }));
            }}
          >
            <div className="relative aspect-square w-full max-w-[min(100%,80vh)] sm:h-[200px] sm:w-[200px]">
              <div className="absolute left-[calc(50%-2px)] top-0 h-full w-[4px] bg-separator"></div>
              <div className="absolute left-0 top-[calc(50%-2px)] h-[4px] w-full bg-separator"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExplorerView;
